import { Directive, ElementRef, HostListener, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Directive to detect invalid date inputs in HTML5 date fields.
 * 
 * HTML5 date inputs return an empty string for both:
 * - Empty/blank fields (valid when optional)
 * - Invalid dates like 02/31 (should be caught as invalid)
 * 
 * This directive checks the native element's validity state to distinguish
 * between these cases and sets a custom validation error when an invalid
 * date is entered.
 */
@Directive({
  selector: 'input[type="date"][formControlName], input[type="date"][formControl]',
  standalone: true
})
export class InvalidDateDetectorDirective {

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() private ngControl: NgControl
  ) {}

  @HostListener('input')
  @HostListener('blur')
  onInputChange(): void {
    this.validateDate();
  }

  private validateDate(): void {
    if (!this.ngControl || !this.ngControl.control) {
      return;
    }

    const inputElement = this.el.nativeElement;
    const control = this.ngControl.control;
    
    // Check if the native input has invalid date format
    // badInput is true when user types invalid date like 02/31
    const hasInvalidDate = inputElement.validity.badInput;
    
    // Get current errors
    const currentErrors = control.errors || {};
    
    if (hasInvalidDate) {
      // Set the invalid date error
      control.setErrors({
        ...currentErrors,
        invalidDate: { message: 'Please enter a valid date or leave empty' }
      });
    } else {
      // Remove the invalid date error if it exists
      if (currentErrors['invalidDate']) {
        const { invalidDate, ...remainingErrors } = currentErrors;
        control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
      }
    }
  }
}
